import Component from '@ember/component';
import EmberObject, {
  action,
  computed,
  get,
  set,
  setProperties,
} from '@ember/object';
import Evented from '@ember/object/evented';
import { run } from '@ember/runloop';
import { htmlSafe } from '@ember/template';

const { FormData, XMLHttpRequest } = window;

// Allows parent controller/component to tell this component to begin the upload
export const BeginUploadEventBus = EmberObject.extend(Evented, {
  beginUpload() {
    this.trigger('beginUpload');
  },
});

/*
  attrs
  - uploadFile - the uploadFile model
  - beginUploadEventBus - instance of a BeginUploadEventBus above
  - showNoFileChosenErrorMessage - when to show an error message

  events
  - onUploadComplete(uploadFile)
    - fires when the upload to AWS is complete
  - onUploadError()
  - onUploadCancelled()
*/
export default Component.extend({
  uploadFile: null,
  beginUploadEventBus: null,
  showNoFileChosenErrorMessage: false,

  fileToUpload: null,
  uploading: false,
  completed: false,
  percentUploaded: 5,
  progressBarStyle: null,

  upload() {
    set(this, 'uploading', true);
    let formData = new FormData();
    formData.append('key', get(this, 'uploadFile.key'));
    formData.append('AWSAccessKeyId', get(this, 'uploadFile.accessKey'));
    formData.append('acl', 'private');
    formData.append('policy', get(this, 'uploadFile.policy'));
    formData.append('signature', get(this, 'uploadFile.signature'));
    formData.append('success_action_status', '201');
    formData.append('file', this.fileToUpload);

    let xhr = new XMLHttpRequest();
    xhr.upload.addEventListener('progress', this.xhrProgress.bind(this), false);
    xhr.addEventListener('load', this.xhrComplete.bind(this), false);
    xhr.addEventListener('error', this.xhrError.bind(this), false);
    xhr.addEventListener('abort', this.xhrCancelled.bind(this), false);
    xhr.open('POST', this.actionUrl, true);
    xhr.send(formData);
  },

  xhrProgress(e) {
    run(() => {
      if (e.lengthComputable) {
        set(this, 'percentUploaded', Math.round((e.loaded * 100) / e.total));
        this.updateProgressBar();
      }
    });
  },

  xhrComplete() {
    run(() => {
      setProperties(this, {
        percentUploaded: 100,
        completed: true,
      });
      this.updateProgressBar();
      this.onUploadComplete(this.uploadFile);
    });
  },

  xhrError(e) {
    run(() => {
      let onUploadError = this.onUploadError;
      if (onUploadError) {
        onUploadError(this.uploadFile, e);
      }
    });
  },

  xhrCancelled() {
    run(() => {
      let onUploadCancelled = this.onUploadCancelled;
      if (onUploadCancelled) {
        onUploadCancelled(this.uploadFile);
      }
    });
  },

  updateProgressBar() {
    set(this, 'progressBarStyle', htmlSafe(`width: ${this.percentUploaded}%`));
  },

  actionUrl: computed(function () {
    return `https://${get(this, 'uploadFile.bucket')}.s3.amazonaws.com`;
  }),

  init() {
    this._super(...arguments);
    this.beginUploadEventBus.on('beginUpload', this, this.upload);
  },

  willDestroyElement() {
    this._super(...arguments);
    this.beginUploadEventBus.off('beginUpload', this, this.upload);
  },

  onChange: action(function (e) {
    let fileToUpload = null;
    if (e.target.files.length === 1) {
      fileToUpload = e.target.files[0];
    }
    set(this, 'uploadFile.fileChosen', fileToUpload != null);
    set(this, 'fileToUpload', fileToUpload);
  }),
});
